<!--
// app/components/pages/players-player/FeaturedPlayerStats.vue
-->
<script setup lang="ts">
import type { PlayerRanking, PlayerRole } from "~/src/player/domain/Player";
import type { SeasonStatsPartner } from "~/src/season-cluster/domain/models/01.Season";
import { setAliasUrl } from "~/src/Shared/utils";
import { $SEASONS_WITHOUT_DATA } from "~/src/season-cluster/infrastructure/ui-services/seasonUiService";

type Props = {
  stats: PlayerRanking[];
  statsPartner?: SeasonStatsPartner;
  playerRole: PlayerRole;
  seasonId: number | undefined;
};
const props = defineProps<Props>();

const seasonCondition = !$SEASONS_WITHOUT_DATA.includes(Number(props.seasonId));

const matchMvp = props.stats.find(s => s.parameter.code === "MVP");
const matchDayMvp = props.stats.find(s => s.parameter.code === "MVP-T");
const redCards = props.stats.find(s => s.parameter.code === "CRT-R");
const yellowCards = props.stats.find(s => s.parameter.code === "CRT-G");
const assist = props.stats.find(s => s.parameter.code === "ASS-V");
const stops = props.stats.find(s => s.parameter.code === "PRT");
const goalsConceded = props.stats.find(s =>
  seasonCondition ? s.parameter.code === "GOL-S" : s.parameter.code === "GOL-ALLS"
);
const generalGoals = props.stats.find(s =>
  seasonCondition ? s.parameter.code === "GOL" : s.parameter.code === "GOL-ALL"
);
</script>

<template>
  <div class="flex gap-2 flex-wrap lg:flex-nowrap">
    <div class="mvp-box">
      <p class="lg:text-center text-secondary !text-lg">{{ $t("player.stat.matchMvp") }}</p>
      <NuxtImg
        v-if="!!statsPartner"
        :src="setAliasUrl(statsPartner?.logo)"
        :alt="statsPartner?.name || ''"
        width="41"
        height="16"
        class="h-4 object-contain lg:absolute top-3 left-3 ms-2 lg:ms-0"
      />
      <p class="lg:text-lg lg:mt-auto text-xs font-bold ms-auto lg:ms-0">
        {{ matchMvp ? matchMvp.total : 0 }}
      </p>
    </div>
    <div class="mvp-box">
      <p class="lg:text-center text-secondary !text-lg">{{ $t("player.stat.matchDayMvp") }}</p>
      <NuxtImg
        v-if="!!statsPartner"
        :src="setAliasUrl(statsPartner?.logo)"
        :alt="statsPartner?.name || ''"
        width="41"
        height="16"
        class="h-4 object-contain lg:absolute top-3 left-3 ms-2 lg:ms-0"
      />
      <p class="lg:text-lg lg:mt-auto text-xs font-bold ms-auto lg:ms-0">
        {{ matchDayMvp ? matchDayMvp.total : 0 }}
      </p>
    </div>

    <div class="stat-box" v-if="playerRole === 'goalkeeper'">
      <p class="text-gray-400 text-sm lg:text-center">
        {{ $t("player.stat.stops") }}
      </p>
      <p class="lg:text-lg lg:mt-auto text-xs font-bold ms-auto lg:ms-0">
        {{ stops ? stops.total : 0 }}
      </p>
    </div>
    <div class="stat-box" v-else>
      <p class="text-gray-400 text-sm lg:text-center">
        {{ $t("player.stat.goals") }}
      </p>
      <p class="lg:text-lg lg:mt-auto text-xs font-bold ms-auto lg:ms-0">
        {{ generalGoals ? generalGoals.total : 0 }}
      </p>
    </div>

    <div class="stat-box" v-if="playerRole === 'goalkeeper'">
      <p class="text-gray-400 text-sm lg:text-center">
        {{ $t("player.stat.goalsConceded") }}
      </p>
      <p class="lg:text-lg lg:mt-auto text-xs font-bold ms-auto lg:ms-0">
        {{ goalsConceded ? goalsConceded.total : 0 }}
      </p>
    </div>
    <div class="stat-box" v-else>
      <p class="text-gray-400 text-sm lg:text-center">
        {{ $t("player.stat.assists") }}
      </p>
      <p class="lg:text-lg lg:mt-auto text-xs font-bold ms-auto lg:ms-0">
        {{ assist ? assist.total : 0 }}
      </p>
    </div>

    <div class="stat-box">
      <p class="text-gray-400 text-sm lg:text-center">
        {{ $t("player.stat.yellowCards") }}
      </p>
      <p class="lg:text-lg lg:mt-auto text-xs font-bold ms-auto lg:ms-0">
        {{ yellowCards ? yellowCards.total : 0 }}
      </p>
    </div>
    <div class="stat-box">
      <p class="text-gray-400 text-sm lg:text-center">
        {{ $t("player.stat.redCards") }}
      </p>
      <p class="lg:text-lg lg:mt-auto text-xs font-bold ms-auto lg:ms-0">
        {{ redCards ? redCards.total : 0 }}
      </p>
    </div>
  </div>
</template>

<style scoped>
.mvp-box {
  @apply rounded-lg px-3 py-3 lg:py-7 bg-gray-800 flex lg:flex-col lg:w-auto w-full lg:relative items-center lg:!flex-[2_2_0%];
}

.stat-box {
  @apply rounded-lg px-3 py-3 lg:py-7 bg-gray-800 flex lg:flex-col lg:w-auto w-full lg:flex-1 lg:relative items-center;
}
</style>
